import React, { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { BTN_PRIMARY, BTN_GHOST, CARD, FIELD } from "@/lib/ui";
import { fmtEuro } from "@/lib/format";
import { Sparkles, Send, Bot, User as UserIcon, FileText, Loader2, TrendingUp } from "lucide-react";

const SUGGESTIONS = [
  "Πόσο ξόδεψα σε μισθούς μέχρι τώρα φέτος;",
  "Γιατί είναι αρνητικό το ταμείο τους καλοκαιρινούς μήνες;",
  "Ποια είναι τα 3 μεγαλύτερα έξοδά μου και πού μπορώ να κόψω;",
  "Πόσο είναι η μισθοδοσία ως ποσοστό των εσόδων;",
];

export default function Assistant() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [forecast, setForecast] = useState(null);
  const endRef = useRef(null);

  const { data: config } = useQuery({ queryKey: ["ai-config"], queryFn: async () => (await api.get("/ai/config")).data });
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming]);

  const runAnalysis = useMutation({
    mutationFn: async () => (await api.post("/ai/analysis", { year: new Date().getFullYear() })).data,
    onSuccess: (d) => { setAnalysis(d); qc.invalidateQueries({ queryKey: ["ai-analyses"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const { data: lastForecast } = useQuery({
    queryKey: ["ai-forecast"],
    queryFn: async () => (await api.get("/ai/forecast/latest")).data,
  });
  const runForecast = useMutation({
    mutationFn: async () => (await api.post("/ai/forecast?months=3")).data,
    onSuccess: (d) => { setForecast(d); toast.success("Η πρόβλεψη ετοιμάστηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("Αποτυχία σύνδεσης με το AI");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.session_id) setSessionId(obj.session_id);
            if (obj.error) toast.error(obj.error);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
            }
          } catch (err) {
            if (process.env.NODE_ENV === "development") {
              console.debug("AI stream: μη αναγνώσιμο πακέτο", payload, err);
            }
          }
        }
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="assistant-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-[#8A8577]">AI Βοηθός</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Οικονομικός Σύμβουλος</h1>
          <p className="text-[#6F6A60] text-sm mt-1">
            Ρωτήστε στα ελληνικά για μισθοδοσία, ταμείο, έσοδα και έξοδα — απαντά με τα πραγματικά δεδομένα σας
            {config?.model ? ` (${config.model})` : ""}. Η μηνιαία έκθεση δημιουργείται αυτόματα κάθε 1η του μήνα.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="ai-forecast-btn" onClick={() => runForecast.mutate()} disabled={runForecast.isPending}
            className={BTN_GHOST}>
            {runForecast.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
            Πρόβλεψη 3 μηνών
          </button>
          <button data-testid="ai-analysis-btn" onClick={() => runAnalysis.mutate()} disabled={runAnalysis.isPending}
            className={BTN_GHOST}>
            {runAnalysis.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            Μηνιαία ανάλυση
          </button>
        </div>
      </div>

      {(forecast || lastForecast) && (() => {
        const f = forecast || lastForecast;
        return (
          <div className={`${CARD} overflow-hidden`} data-testid="ai-forecast-result">
            <div className="px-5 py-4 border-b border-[#E3DED4]">
              <h2 className="font-medium text-[#45423C]">Πρόβλεψη εσόδων ανά βίλα — {(f.periods || []).join(" · ")}</h2>
              {f.summary && <p className="text-sm text-[#6F6A60] mt-1">{f.summary}</p>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm whitespace-nowrap">
                <thead>
                  <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                    <th className="px-4 py-3">Μονάδα / Βίλα</th>
                    {(f.periods || []).map((p) => <th key={p} className="px-4 py-3 text-right">{p}</th>)}
                    <th className="px-4 py-3 text-right">Σύνολο</th>
                    <th className="px-4 py-3">Σχόλιο</th>
                  </tr>
                </thead>
                <tbody>
                  {(f.units || []).map((u) => {
                    const total = (u.months || []).reduce((s, m) => s + (m.revenue || 0), 0);
                    return (
                      <tr key={u.unit} data-testid={`forecast-row-${u.unit}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                        <td className="px-4 py-2 font-medium text-[#45423C]">{u.unit}</td>
                        {(f.periods || []).map((p) => {
                          const m = (u.months || []).find((x) => x.month === p);
                          return (
                            <td key={p} className="px-4 py-2 text-right tabular">
                              {m ? fmtEuro(m.revenue) : "—"}
                              {m?.confidence && <span className="block text-[10px] text-[#6F6A60]">{m.confidence}</span>}
                            </td>
                          );
                        })}
                        <td className="px-4 py-2 text-right tabular font-semibold">{fmtEuro(total)}</td>
                        <td className="px-4 py-2 text-xs text-[#6F6A60] whitespace-normal max-w-[260px]">{u.note || "—"}</td>
                      </tr>
                    );
                  })}
                  {!(f.units || []).length && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-[#6F6A60]">Χωρίς δεδομένα πρόβλεψης.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {analysis && (
        <div className={`${CARD} p-5`} data-testid="ai-analysis-result">
          <h2 className="font-medium text-[#45423C] mb-2">Ανάλυση — {analysis.period}</h2>
          <div className="whitespace-pre-wrap text-sm text-[#3A3833] leading-relaxed">{analysis.text}</div>
        </div>
      )}

      <div className={`${CARD} overflow-hidden`}>
        <div className="max-h-[52vh] min-h-[280px] overflow-y-auto p-5 space-y-4" data-testid="ai-messages">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <Sparkles className="h-6 w-6 text-[#8A8577] mx-auto" />
              <p className="text-sm text-[#6F6A60] mt-3">Ξεκινήστε με μια ερώτηση:</p>
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {SUGGESTIONS.map((s, i) => (
                  <button key={s} data-testid={`ai-suggestion-${i}`} onClick={() => send(s)}
                    className="rounded-full border border-[#E3DED4] bg-white px-3 py-1.5 text-xs text-[#6F6A60] transition-colors hover:border-[#B9AE9A] hover:text-[#45423C]">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={m.id ?? `${m.role}-${i}`} data-testid={`ai-msg-${m.role}-${i}`}
              className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && (
                <div className="h-8 w-8 shrink-0 rounded-lg bg-[#45423C] flex items-center justify-center">
                  <Bot className="h-4 w-4 text-[#8A8577]" />
                </div>
              )}
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                m.role === "user" ? "bg-[#45423C] text-white" : "bg-[#F7F5F1] border border-[#E3DED4] text-[#3A3833]"}`}>
                {m.content || (streaming && i === messages.length - 1
                  ? <span className="inline-flex items-center gap-2 text-[#6F6A60]"><Loader2 className="h-3 w-3 animate-spin" /> σκέφτομαι…</span>
                  : "")}
              </div>
              {m.role === "user" && (
                <div className="h-8 w-8 shrink-0 rounded-lg bg-[#B9AE9A] flex items-center justify-center">
                  <UserIcon className="h-4 w-4 text-[#45423C]" />
                </div>
              )}
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="border-t border-[#E3DED4] p-4 flex items-end gap-2">
          <input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Ρωτήστε κάτι, ${user?.name?.split(" ")[0] || ""}…`} className={FIELD} />
          <button data-testid="ai-send" onClick={() => send()} disabled={streaming || !input.trim()} className={BTN_PRIMARY}>
            <Send className="h-4 w-4" /> Αποστολή
          </button>
        </div>
      </div>
    </div>
  );
}
